import { createImmutableObjectClass } from 'immutable-less/ImmutableObject';
export default class CurvePoints extends createImmutableObjectClass({
  p0x: 0,
  p0y: 0,
  p1x: 0,
  p1y: 0,
  p2x: 0,
  p2y: 0,
  p3x: 0,
  p3y: 0
}) {
  split(numberOfSegments) {
    return splitCurve({
      p0: [this.p0x, this.p0y],
      p1: [this.p1x, this.p1y],
      p2: [this.p2x, this.p2y],
      p3: [this.p3x, this.p3y]
    }, numberOfSegments).map(({
      p0: [p0x, p0y],
      p1: [p1x, p1y],
      p2: [p2x, p2y],
      p3: [p3x, p3y]
    }) => new CurvePoints({
      p0x,
      p0y,
      p1x,
      p1y,
      p2x,
      p2y,
      p3x,
      p3y
    }));
  }
}

/**
 * enumerates overlapping slices of a given length
 *
 * example:
 * windows([1, 2, 3, 4, 5], 3) = [[1, 2, 3], [2, 3, 4], [3, 4, 5]]
 */
function windows(array, length) {
  return array.slice(Math.max(0, length - 1)).map((_, index) => array.slice(index, index + length));
}

/**
 * linear interpolation, or "lerp". mixes numbers a and b where t represents the
 * desired ratio of b to a.
 *
 * example, using percentages to format the ratios (100% == 1):
 * lerp(40, 1=0, 25%) = 40 * 75% + 100 * 25% = 30 + 25 = 55
 */
// eslint-disable-next-line id-length
function lerp(numberA, numberB, t) {
  return numberA * (1 - t) + numberB * t;
}
function lerpPoints([p1x, p1y], [p2x, p2y],
// eslint-disable-next-line id-length
t) {
  return [lerp(p1x, p2x, t), lerp(p1y, p2y, t)];
}

/**
 * Points on a Bézier curve are drawn by reducing the lines connecting the four
 * control points to a single line using linear interpolation. This line
 * represents a tangent of the curve.  We use linear interpolation once more to
 * pick the point on that line which belongs on the curve.  This is done for all
 * values of t where 0 <= t <= 1.
 *
 * To split the curve into segments, we make a "cut" at each point on the line
 * where the segments will terminate. Each cut has a value for t, a point on the
 * curve, and a tangent. The control points for the curve segments can be
 * calculated using their adjacent cuts.
 *
 * Fun fact: The line p0 -> p1 is the curve's tangent at t = 0, and the line
 * p2 -> p3 is the curve's tangent at t = 1
 */
function tangentOnCurve(p0, p1, p2, p3,
// eslint-disable-next-line id-length
t) {
  const p0p1 = lerpPoints(p0, p1, t);
  const p1p2 = lerpPoints(p1, p2, t);
  const p2p3 = lerpPoints(p2, p3, t);
  return [lerpPoints(p0p1, p1p2, t), lerpPoints(p1p2, p2p3, t)];
}
function splitCurve({
  p0,
  p1,
  p2,
  p3
}, numSegments) {
  const cuts = new Array(numSegments + 1).fill(undefined).map((_, index) => index / numSegments)
  // eslint-disable-next-line id-length
  .map(t => {
    const tangent = tangentOnCurve(p0, p1, p2, p3, t);
    return {
      t,
      tangent,
      point: lerpPoints(...tangent, t)
    };
  });
  return windows(cuts, 2).map(([prevCut, nextCut]) => ({
    p0: prevCut.point,
    p1: lerpPoints(...prevCut.tangent, nextCut.t),
    p2: lerpPoints(...nextCut.tangent, prevCut.t),
    p3: nextCut.point
  }));
}